import { Box, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { LogUsageTable } from "./LogUsageTable";
import { LogUsageItem } from "./LogUsageItem";
import { LogUsageChart } from "./LogUsageChart";
import { useEffect, useState } from "react";
import { TabPanel } from "../../components/TabPanel";
import { LOG_USAGE_PERIOD } from "../../utils/constants";
import { getDaysInMonth, getHoursInDay } from "../../utils/date";
import moment from "moment";
import { useFetchData } from "../../hooks/useFetchData";
import { useParams } from "react-router-dom";
import { humanFileSize, shortenNumber } from "../../utils/number";
import { DatePicker } from "@mui/x-date-pickers";

const formatMemoryString = (memory, usage) => {
    const parsedData = parseFloat(memory);

    let bytesInUnit = 0;

    if(usage === "count") {
        return parsedData ? shortenNumber(parsedData) : 0;
    } else if(usage === "total_size" || usage === "average_size" || usage === "max_size") {
        bytesInUnit = 1000;
    } else {
        return parsedData ?? 0;
    }

    return parsedData ? humanFileSize(parsedData * bytesInUnit, true) : 0;
};

const clusterLogUsageColumns = [
    { field: 'transaction_name', headerName: 'Cluster', width: 400 },
    { field: 'total_size', headerName: 'Usage', width: 400, valueGetter: params => {
        return formatMemoryString(params.row.total_size, "total_size");
    }},
];

const loginColumns = [
    { field: 'transaction_name', headerName: 'Channel', width: 400 },
    { field: 'total_size', headerName: 'Usage', width: 400, valueGetter: params => {
        return formatMemoryString(params.row.total_size, "total_size");
    }},
];

const getDates = (date, timePeriod) => {
    let dates = [];

    if(timePeriod === LOG_USAGE_PERIOD.HOURLY) {
        dates = getHoursInDay(date.format("YYYY"), date.format("MM"), date.format("DD"));
    } else if(timePeriod === LOG_USAGE_PERIOD.DAILY) {
        dates = getDaysInMonth(date.format("YYYY"), date.format("MM"));
    } else if(timePeriod === LOG_USAGE_PERIOD.MONTHLY) {
        dates = getDaysInMonth(date.format("YYYY"));
    }

    return dates
}

const logUsageDetailedColumns = (date, timePeriod) => {
    const dates = getDates(date, timePeriod);

    return [
        { field: 'transaction_name', headerName: 'Metric', width: 200 },
        ...dates.map(date => {
            return { 
                field: date, 
                headerName: date, 
                valueGetter: ({ row }) => {
                    return formatMemoryString(row?.[date]?.[row?.usage], row?.usage);
                },
                width: 200 
            };
        })
    ]
};

const logUsageChartData = (data, queryName, transactionName, date, timePeriod, metric) => {
    const transactionData = data?.find(query => query.query_name === queryName)?.data?.find(transaction => transaction.transaction_name === transactionName) || [];

    const dates = getDates(date, timePeriod);

    return [
        ["Date", metric],
        ...dates.map(date => {
            return [date, transactionData?.[date]?.[metric] ?? 0];
        })
    ];
};

const capabilities = [
    {
        capability_label: "Servicing",
        capability_name: "servicing"
    },
    {
        capability_label: "Money Movement",
        capability_name: "money_movement"
    },
    {
        capability_label: "Auth",
        capability_name: "auth"
    },
]

export const LogUsage = () => {
    const [totalLogUsageSelectedApp, setTotalLogUsageSelectedApp] = useState([0]);
    const [maxSizeMessageSelectedApp, setMaxSizeMessageSelectedApp] = useState([0]);
    const [timePeriod, setTimePeriod] = useState(LOG_USAGE_PERIOD.DAILY);
    const [dataDate, setDataDate] = useState(moment());
    const [tabIndex, setTabIndex] = useState(0);
    const [params, setParams] = useState({date: dataDate});

    const handleTotalLogUsageRowSelection = (row) => {
        setTotalLogUsageSelectedApp(row);
    };

    const handleMaxSizeMessageRowSelection = (row) => {
        setMaxSizeMessageSelectedApp(row);
    };

    const handleTabChange = (event, newTabIndex) => {
        setTabIndex(newTabIndex);
    };

    const onTimePeriodChange = (e) => {
        setTimePeriod(e?.target?.value);
    }

    const onDataDateChange = (value) => {
        setDataDate(value);
    }

    const { status: dailyDataStatus, data: dailyData } = useFetchData({
        endpoint: `logUsage/${timePeriod}`,
        params: params,
        defaultData: []
    });

    useEffect(() => {
        setParams({
            date: dataDate
        });
    }, [dataDate]);

    // const dailyData = testData;

    return (
        <Grid container justifyContent="center" sx={{ px: 10, mt: 0 }} spacing={1}>
            <Grid item xs={12} md={12}>
                <LogUsageItem sx={{display: 'flex', justifyContent: 'end', alignItems: 'center'}}>
                    <Box
                        sx={{ mr: "auto", ml: 2 }}
                    >
                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            align="left"
                            alignSelf="center"
                            component="div"
                        >
                            Log Usage
                        </Typography>

                        {/* <Typography
                            variant="body1"
                            align="left"
                            alignSelf="center"
                            component="div"
                        >
                            Time Frame: 8-9am CT
                        </Typography> */}
                    </Box>

                    {timePeriod === LOG_USAGE_PERIOD.HOURLY && <DatePicker
                        sx={{ ml: "auto" }}
                        label={
                            'Day Month Year'
                        }
                        views={
                            ['year', 'month', 'day']
                        }
                        value={dataDate}
                        onChange={onDataDateChange}
                    />}

                    {timePeriod === LOG_USAGE_PERIOD.DAILY && <DatePicker
                        sx={{ ml: "auto" }}
                        label={
                            'Month Year'
                        }
                        views={
                            ['year', 'month']
                        }
                        value={dataDate}
                        onChange={onDataDateChange}
                    />}

                    {timePeriod === LOG_USAGE_PERIOD.MONTHLY && <DatePicker
                        sx={{ ml: "auto" }}
                        label={
                            'year'
                        }
                        views={
                            ['year']
                        }
                        value={dataDate}
                        onChange={onDataDateChange}
                    />}

                    <FormControl variant="outlined" sx={{ ml: 2, minWidth: 200 }}>
                        <InputLabel id="select-time-period">Time Period</InputLabel>

                        <Select
                            variant="outlined"
                            labelId="select-filter-type"
                            value={timePeriod}
                            label="Time Period"
                            autoWidth
                            onChange={onTimePeriodChange}>
                            <MenuItem value={LOG_USAGE_PERIOD.HOURLY}>Hourly</MenuItem>
                            <MenuItem value={LOG_USAGE_PERIOD.DAILY}>Daily</MenuItem>
                            <MenuItem value={LOG_USAGE_PERIOD.MONTHLY}>Monthly</MenuItem>
                        </Select>
                    </FormControl>
                </LogUsageItem>
            </Grid>

            <Grid item xs={6} md={6} >
                <LogUsageItem title="Overall Digital Log Usage by Cluster">
                    <LogUsageTable columns={clusterLogUsageColumns} rows={dailyData?.find(query => query.query_name.includes("overall_digital_log_usage"))?.data || []}/>
                </LogUsageItem>
            </Grid>

            <Grid item xs={6} md={6} >
                <LogUsageItem title="Login">
                    <LogUsageTable columns={loginColumns} rows={dailyData?.filter(query => query.query_name.includes("login_"))?.map((query, index) => { return { ...query.data[0], id: index }}) || []}/>
                </LogUsageItem>
            </Grid>

            <Grid item xs={12} md={12}>
                <LogUsageItem title="Log Usage by Key Capabilities">
                    <Stack spacing={2}>
                        <LogUsageTable columns={logUsageDetailedColumns(dataDate, timePeriod)} rows={dailyData?.filter(query => query.query_name.includes("log_usage_by_capability"))?.map((query, index) => { return { ...query.data[0], id: index, usage: "total_size"}}) || []}/>

                        <Grid container>
                            <Grid item md={4}>
                                <LogUsageItem title="Servicing" cardWrapper={false}>
                                    <LogUsageChart data={logUsageChartData(dailyData, "log_usage_by_capability_servicing", "servicing", dataDate, timePeriod, "total_size")}/>
                                </LogUsageItem>
                            </Grid>
                            <Grid item md={4}>
                                <LogUsageItem title="Money Movement" cardWrapper={false}>
                                    <LogUsageChart data={logUsageChartData(dailyData, "log_usage_by_capability_money_movement", "money_movement", dataDate, timePeriod, "total_size")}/>
                                </LogUsageItem>
                            </Grid>
                            <Grid item md={4}>
                                <LogUsageItem title="Auth" cardWrapper={false}>
                                    <LogUsageChart data={logUsageChartData(dailyData, "log_usage_by_capability_auth", "auth", dataDate, timePeriod, "total_size")}/>
                                </LogUsageItem>
                            </Grid>
                        </Grid>
                    </Stack>
                </LogUsageItem>
            </Grid>

            <Grid item xs={12} md={12} >
                <LogUsageItem>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth" aria-label="api status tabs">
                        {capabilities?.map((capability, index) => {
                            return <Tab label={capability.capability_label} id={`simple-tab-${index}`} aria-controls={`simple-tabpanel-${index}`} />;
                        })}
                        </Tabs>
                    </Box>

                    {capabilities?.map((capability, index) => {
                        return (
                            <TabPanel value={tabIndex} index={index}>
                                <Stack spacing={2}>
                                    <LogUsageItem title="Log Usage Summary">
                                        <Stack spacing={2}>
                                            <LogUsageItem cardWrapper={false}>
                                                <LogUsageTable columns={logUsageDetailedColumns(dataDate, timePeriod)} rows={[
                                                    { 
                                                        ...dailyData?.find(query => query.query_name === `log_usage_by_capability_${capability.capability_name}`)?.data[0],
                                                        transaction_name: "Count of Messages",
                                                        usage: "count",
                                                        id: 0
                                                    },
                                                    { 
                                                        ...dailyData?.find(query => query.query_name === `log_usage_by_capability_${capability.capability_name}`)?.data[0],
                                                        transaction_name: "Total Log Usage",
                                                        usage: "total_size",
                                                        id: 1
                                                    },
                                                    { 
                                                        ...dailyData?.find(query => query.query_name === `log_usage_by_capability_${capability.capability_name}`)?.data[0],
                                                        transaction_name: "Avg Size/Message",
                                                        usage: "average_size",
                                                        id: 2
                                                    }
                                                ] || []}/>
                                            </LogUsageItem>

                                            <LogUsageItem cardWrapper={false}>
                                                <LogUsageChart data={logUsageChartData(dailyData, `log_usage_by_capability_${capability.capability_name}`, capability.capability_name, dataDate, timePeriod, "total_size")}/>
                                            </LogUsageItem>
                                        </Stack>
                                    </LogUsageItem>

                                    <LogUsageItem title="Total Log Usage by App Name For Top 10">
                                        <Stack spacing={2}>
                                            <LogUsageItem cardWrapper={false}>
                                                <LogUsageTable selectedRow={totalLogUsageSelectedApp} onRowSelection={handleTotalLogUsageRowSelection} columns={logUsageDetailedColumns(dataDate, timePeriod)} rows={dailyData?.find(query => query.query_name === `log_usage_by_app_name_${capability.capability_name}`)?.data?.map(transaction => { return { ...transaction, usage: "total_size"}}) || []}/>
                                            </LogUsageItem>

                                            <LogUsageItem title={(dailyData?.find(query => query.query_name === `log_usage_by_app_name_${capability.capability_name}`)?.data?.map(transaction => { return { ...transaction, usage: "total_size"}}) || [])?.[totalLogUsageSelectedApp?.[0]]?.transaction_name} cardWrapper={false}>
                                                <LogUsageChart data={logUsageChartData(dailyData, `log_usage_by_app_name_${capability.capability_name}`, (dailyData?.find(query => query.query_name === `log_usage_by_app_name_${capability.capability_name}`)?.data?.map(transaction => { return { ...transaction, usage: "total_size"}}) || [])?.[totalLogUsageSelectedApp?.[0]]?.transaction_name, dataDate, timePeriod, "count")}/>
                                            </LogUsageItem>
                                        </Stack>
                                    </LogUsageItem>

                                    <LogUsageItem title="Max Size For A Message By App Name For Top 10">
                                        <Stack spacing={2}>
                                            <LogUsageItem cardWrapper={false}>
                                                <LogUsageTable selectedRow={maxSizeMessageSelectedApp} onRowSelection={handleMaxSizeMessageRowSelection} columns={logUsageDetailedColumns(dataDate, timePeriod)} rows={dailyData?.find(query => query.query_name === `log_usage_by_app_name_${capability.capability_name}`)?.data?.map(transaction => { return { ...transaction, usage: "max_size"}}) || []}/>
                                            </LogUsageItem>

                                            <LogUsageItem title={(dailyData?.find(query => query.query_name === `log_usage_by_app_name_${capability.capability_name}`)?.data?.map(transaction => { return { ...transaction, usage: "max_size"}}) || [])?.[maxSizeMessageSelectedApp?.[0]]?.transaction_name} cardWrapper={false}>
                                                <LogUsageChart data={logUsageChartData(dailyData, `log_usage_by_app_name_${capability.capability_name}`, (dailyData?.find(query => query.query_name === `log_usage_by_app_name_${capability.capability_name}`)?.data?.map(transaction => { return { ...transaction, usage: "max_size"}}) || [])?.[maxSizeMessageSelectedApp?.[0]]?.transaction_name, dataDate, timePeriod, "count")}/>
                                            </LogUsageItem>
                                        </Stack>
                                    </LogUsageItem>
                                </Stack>
                            </TabPanel>
                        );
                    })}
                </LogUsageItem>
            </Grid>
        </Grid>
    );
};